import { useState } from "react";
import { useAuth } from "../lib/auth";
import { SectionHead } from "./SectionHead";

// The five-field comparable capture form (order item 2): property · stage · price · whose-NOI ·
// date. No wizard ("thirty seconds"). A free-text property that isn't in the corpus is ACCEPTED
// and HELD (a lost close is worse than an unlinked one). Comparables are observed market evidence
// for the Underwriter's table — captured, never applied. Price is the FACT; the cap rate is
// derived (noi/price) only when an NOI and its source are given.
const STAGES = ["offer", "counter", "close"];
const NOI_SOURCES = [
  { v: "unknown", label: "unknown (rate left absent, never guessed)" },
  { v: "our_extraction", label: "our extraction" },
  { v: "seller_om", label: "seller OM" },
  { v: "buyer_underwriting", label: "buyer underwriting" },
];

export default function ComparableEvidence() {
  const { authedFetch } = useAuth();
  const [f, setF] = useState({
    property_ref: "", stage: "close", price_usd: "", noi_usd: "",
    noi_source: "unknown", event_date: "",
  });
  const [err, setErr] = useState("");
  const [saved, setSaved] = useState(null);
  const [busy, setBusy] = useState(false);

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setSaved(null); setBusy(true);
    try {
      const body = {
        property_ref: f.property_ref.trim(),
        stage: f.stage,
        price_usd: Number(f.price_usd),
        event_date: f.event_date,
        noi_source: f.noi_source,
      };
      if (f.noi_source !== "unknown" && f.noi_usd !== "") body.noi_usd = Number(f.noi_usd);
      const r = await authedFetch("/comparables", { method: "POST", body });
      setSaved(r);
      setF((p) => ({ ...p, property_ref: "", price_usd: "", noi_usd: "", event_date: "" }));
    } catch (e2) {
      setErr(e2.detail || "Could not capture this comparable.");
    } finally { setBusy(false); }
  };

  const noiDisabled = f.noi_source === "unknown";

  return (
    <div data-testid="comparable-evidence-view">
      <SectionHead eyebrow="THE UNDERWRITER · COMPARABLE EVIDENCE" testid="comp"
        headline="Observed evidence, captured and never applied."
        lede="Price is the fact; the cap rate is derived only when an NOI and its source are given." />

      <form className="comp-form" onSubmit={submit} data-testid="comparable-form">
        <label>Property</label>
        <input data-testid="comp-property" value={f.property_ref} onChange={set("property_ref")}
               placeholder="p-05, or a free-text name (held if not in the corpus)" required />

        <label>Stage</label>
        <select data-testid="comp-stage" value={f.stage} onChange={set("stage")}>
          {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>

        <label>Price (USD) — the fact</label>
        <input data-testid="comp-price" type="number" min="1" step="any"
               value={f.price_usd} onChange={set("price_usd")} placeholder="6464300" required />

        <label>Whose NOI</label>
        <select data-testid="comp-noi-source" value={f.noi_source} onChange={set("noi_source")}>
          {NOI_SOURCES.map((n) => <option key={n.v} value={n.v}>{n.label}</option>)}
        </select>

        <label>NOI (USD){noiDisabled ? " — omit while source is unknown" : ""}</label>
        <input data-testid="comp-noi" type="number" min="0" step="any"
               value={f.noi_usd} onChange={set("noi_usd")} disabled={noiDisabled}
               placeholder={noiDisabled ? "—" : "452501"} />

        <label>Date</label>
        <input data-testid="comp-date" type="date" value={f.event_date} onChange={set("event_date")} required />

        <button type="submit" className="btn-primary" data-testid="comp-submit" disabled={busy}>
          {busy ? "Capturing…" : "Capture comparable"}
        </button>
      </form>

      {err && <div className="error" data-testid="comp-error">{err}</div>}
      {saved && (
        <div className="banner" data-testid="comp-saved">
          Captured “{saved.property_ref}” · {saved.stage} · ${Number(saved.price_usd).toLocaleString()}
          {saved.implied_cap_rate_pct != null
            ? ` · implied ${saved.implied_cap_rate_pct}%`
            : " · rate absent (NOI source unknown — not guessed)"}
        </div>
      )}
    </div>
  );
}
